import { useState } from "react";
import { createPortal } from "react-dom";
import { useToasts, type Toast } from "../../store/toasts";
import { useT } from "../../lib/texte";
import { Icon } from "./Icon";
import { IconButton, portalRoot } from "./primitives";

const FARBE: Record<string, string> = {
  success: "var(--good)",
  error: "var(--error)",
  info: "var(--accent-text)",
  warn: "var(--warn)",
};
const SYMBOL: Record<string, string> = {
  success: "check-circle",
  error: "warning-circle",
  info: "info",
  warn: "warning",
};

function Meldung({ t: meldung, onClose }: { t: Toast; onClose: () => void }) {
  const [kopiert, setKopiert] = useState(false);
  const { t } = useT();
  const farbe = FARBE[meldung.typ] ?? FARBE.info;
  const symbol = SYMBOL[meldung.typ] ?? "info";

  const kopieren = () => {
    if (!meldung.copy) return;
    void navigator.clipboard?.writeText(meldung.copy).then(() => {
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1600);
    });
  };

  return (
    <div
      className="glass-strong anim-slide-r"
      // role="status" statt "alert": die Vorlesehilfe liest die Meldung zu
      // Ende gesprochener Sätze vor, statt sie zu unterbrechen. Fehler bleiben
      // ohnehin zwölf Sekunden stehen (store/toasts.ts).
      role="status"
      style={{ borderRadius: "var(--r-knopf)", padding: "13px 15px", boxShadow: "var(--shadow-lg)", display: "flex", gap: 11, alignItems: "flex-start", borderLeft: `3px solid ${farbe}` }}
    >
      <Icon name={symbol} size={19} weight="fill" style={{ color: farbe, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>{meldung.msg}</p>
        {meldung.detail && (
          <p className={meldung.mono ? "font-mono" : ""} style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 3, wordBreak: "break-word" }}>
            {meldung.detail}
          </p>
        )}
        {meldung.aktion && (
          <button
            type="button"
            className="ring-focus"
            onClick={() => {
              meldung.aktion?.onClick();
              onClose();
            }}
            style={{ marginTop: 7, marginRight: 14, fontSize: 12, fontWeight: 600, color: "var(--accent-text)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}
          >
            {meldung.aktion.label}
          </button>
        )}
        {meldung.copy && (
          <button
            type="button"
            onClick={kopieren}
            style={{
              marginTop: 7,
              fontSize: 11.5,
              fontWeight: 600,
              color: kopiert ? "var(--good)" : "var(--accent-text)",
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: 0,
              fontFamily: "inherit",
            }}
          >
            <Icon name={kopiert ? "check" : "copy"} size={13} />
            {kopiert ? t.allgemein.kopiert : t.allgemein.meldungKopieren}
          </button>
        )}
      </div>
      <IconButton icon="x" size={14} box={24} onClick={onClose} title={t.allgemein.schliessen} />
    </div>
  );
}

/**
 * Gehört GENAU EINMAL in den Wurzelbaum (App.tsx), nicht je Bildschirm.
 *
 * Er sitzt unten rechts, aber über der Reiterleiste des Telefons: deren Höhe
 * steht als `--leiste-frei` in base.css, damit hier keine zweite Zahl gepflegt
 * werden muss.
 */
export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const removeToast = useToasts((s) => s.removeToast);
  return createPortal(
    <div className="toaster" aria-live="polite">
      {toasts.map((t) => (
        <Meldung key={t.id} t={t} onClose={() => removeToast(t.id)} />
      ))}
    </div>,
    portalRoot(),
  );
}
